import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, computed, inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map, switchMap, take } from 'rxjs';

import { ReallocationV2Store } from '../../state/reallocation-v2.store';
import { isBuyableSecurity, ReallocationV2DataService } from '../../services/reallocation-v2-data.service';
import { MatDialog } from '@angular/material/dialog';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import { Position, SecurityOption } from '../../models/reallocation-v2.models';
import {
  ShareCalculatorData,
  ShareCalculatorModalComponent,
} from '../share-calculator-modal/share-calculator-modal.component';

/**
 * Shortest term worth a round trip, and the shortest worth offering to hand-enter.
 *
 * Kept in step with the service's own guard deliberately: below this it does not search at
 * all, so results are empty for a reason that is not "nothing matched", and offering
 * `Add "A" manually` against a list nobody has looked up would be the same false dead end
 * the commit-on-blur used to produce.
 */
const MIN_SEARCH_TERM_LENGTH = 2;

/**
 * One row of the dropdown: a security to pick, or the escape hatch at the bottom.
 *
 * A single list so the keyboard, the highlight and Enter cannot disagree about what is
 * where. Tracking the manual row separately is how it ends up taken by an Enter that was
 * aimed at a security.
 */
type PickerOption =
  | { readonly kind: 'security'; readonly security: SecurityOption }
  | { readonly kind: 'manual' };

/**
 * One spelling for a search term, so a stored term and a typed one compare honestly.
 * Ticker input is case- and whitespace-insensitive everywhere else in this flow.
 */
function searchKey(term: string | null): string {
  return (term ?? '').trim().toUpperCase();
}

/**
 * Picker state captured at the moment a search ends, so the commit that follows reasons
 * about how the search *was* rather than about live signals it has already reset.
 *
 * ⚠️ Down to what the CLOSE needs to know, from five fields to two. It used to carry the
 * results, whether a lookup was in flight, and which term those results answered, because
 * closing RESOLVED the typed text -- exact match to a real security, anything else to a
 * hand-typed one -- and that decision could not be made against a stale or absent list.
 * Closing decides nothing now, so none of it is needed.
 */
interface PendingCommit {
  /** The security the row held before {@link BuySecuritiesStepComponent.reopen} cleared it. */
  previous: SecurityOption | null;
  /** Whether the advisor typed during this search, as opposed to a seeded term. */
  dirty: boolean;
}

/**
 * Step 2 — Buy Additional Securities: securities the account does not already hold, or more of ones it does.
 *
 * Shares are entered, the price is Orion's and read-only, and the estimated amount is
 * derived. That ordering is the point: nothing downstream has to guess what the advisor
 * meant, which is what makes the request safe for the trade team — and later for
 * automated write-back — to act on.
 *
 * The security cell is a combobox rather than a button-then-dialog: an empty row opens
 * ready to type, and the results are keyboard-navigable, because adding several buys is
 * a typing task and reaching for the mouse between each one is the slow part.
 *
 * **A typed ticker is accepted even when nothing matches it.** Eclipse's security master
 * does not carry everything an advisor may legitimately want to buy, and refusing the
 * unmatched case would push them out of the wizard to file the request another way. So
 * closing the picker with unmatched text commits it (see {@link commitSearch}) as a
 * security with a **null id** — which is what marks it for the trade team, on the row,
 * again on Review & Submit, and finally as a trade order with no `OrionEclipseSecurityID`.
 *
 * What it must never do is look like a resolved buy. Escape still cancels outright, so
 * "I typed this by mistake" and "I mean this ticker" stay separate gestures.
 */
@Component({
  selector: 'app-rav2-buy-securities-step',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectOnFocusDirective, TwoDecimalsDirective],
  templateUrl: './buy-securities-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuySecuritiesStepComponent {
  readonly store = inject(ReallocationV2Store);
  private readonly dataService = inject(ReallocationV2DataService);

  private readonly dialog = inject(MatDialog);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Which row's results are showing. Only one at a time: a row becomes active when its
   * input is typed in, so the list always belongs to whatever is under the cursor.
   */
  readonly searchingRowId = signal<string | null>(null);
  readonly searchResults = signal<readonly SecurityOption[]>([]);
  readonly isSearching = signal(false);
  /**
   * Which term {@link searchResults} answers, normalised by {@link searchKey}.
   *
   * Null until the first result arrives, and reset with the picker. Read by
   * {@link commitSearch} to tell a real empty result from one nobody has asked for.
   */
  private readonly resultsTerm = signal<string | null>(null);
  /**
   * Index into {@link options} that Enter or Tab would take; **-1 when nothing is highlighted**.
   *
   * ⚠️ Indexes the COMBINED list, not `searchResults`, and the default is the first SECURITY
   * rather than index 0. That is what keeps `Add "…" manually` off the highlight now that it
   * sits at the TOP of the list: with results it starts one below, and with none there is no
   * security to land on, so it stays at -1 and the manual row waits to be aimed at.
   */
  readonly highlighted = signal(-1);

  /**
   * Search text per row. Held here rather than on the store's `BuyRow` because it is
   * view state — a half-typed ticker is not part of the request.
   */
  readonly terms = signal<Readonly<Record<string, string>>>({});

  /**
   * The security a row held before {@link reopen} cleared it to search again.
   *
   * Restored if the picker then closes without a choice. Otherwise "click the security to
   * change it, then change your mind" empties the row — the advisor loses a valid
   * selection by looking at it.
   */
  private previousSecurity: SecurityOption | null = null;

  /**
   * Whether the advisor has typed since this search opened.
   *
   * {@link reopen} seeds the box with the ticker already on the row, so a non-empty term
   * is not on its own evidence of intent — committing it blindly would turn a resolved
   * security into an unresolved one just for being looked at.
   */
  private searchDirty = false;


  /** Drives the debounced search; one stream, feeding whichever row is active. */
  private readonly searchControl = new FormControl<string>('', { nonNullable: true });

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isSearching.set(true);
          // The term travels WITH its results so the two can never be read apart. What
          // arrives is an answer to a specific question, and `commitSearch` has to be able
          // to ask whether it is the question currently on screen.
          return this.dataService.searchSecurities(term).pipe(map((results) => ({ term, results })));
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ term, results }) => {
        this.searchResults.set(results);
        this.resultsTerm.set(searchKey(term));
        // ⚠️ BEFORE the highlight: `options` is withheld while a lookup is out, so reading it
        // above this line would find an empty list and park the highlight at -1 on results
        // that had in fact arrived.
        this.isSearching.set(false);
        // First real security, never the manual row above it. `findIndex` returns -1 when
        // nothing matched, which is exactly the wanted default -- see `highlighted`.
        this.highlighted.set(this.options().findIndex((o) => o.kind === 'security'));
      });
  }

  /**
   * What the advisor typed, falling back to the text the row was left holding.
   *
   * The fallback is what makes unresolved text survive the picker closing: nothing is
   * committed on the way out any more, so the term has to come back into the box when they
   * return to the row. See {@link BuyRow.pendingTerm}.
   */
  termFor(rowId: string): string {
    const live = this.terms()[rowId];
    if (live !== undefined) return live;
    return this.store.buyRows().find((r) => r.id === rowId)?.pendingTerm ?? '';
  }

  /**
   * Whether to offer `Add "…" manually` under the results.
   *
   * ⚠️ Keyed on **no EXACT match**, not on an empty list, and the difference is a real case
   * rather than a nicety. CUSIPs from one issuer share their first six characters, and
   * `matchRank` ranks ticker-prefix matches -- so typing a full CUSIP can bring back a
   * SIBLING CUSIP and nothing else. Results are not empty, the advisor's own security is
   * not among them, and an empty-list rule would leave them with no way out and a
   * plausible wrong answer sitting highlighted.
   *
   * The cost is that a half-typed term shows it too ("AAP" offers `Add "AAP" manually`).
   * Acceptable: it is last, never auto-highlighted, and quotes back exactly what is in the
   * box, so it reads as an escape hatch rather than a suggestion.
   *
   * Withheld while a lookup is out. Offering it against results that have not arrived is
   * the same mistake the old commit-on-blur made -- calling "nobody has asked yet" a miss.
   */
  readonly canAddManually = computed(() => {
    const rowId = this.searchingRowId();
    if (!rowId || this.isSearching()) return false;

    const term = this.termFor(rowId).trim();
    if (term.length < MIN_SEARCH_TERM_LENGTH) return false;
    if (this.resultsTerm() !== searchKey(term)) return false;

    const wanted = term.toUpperCase();
    return !this.searchResults().some((r) => r.ticker.trim().toUpperCase() === wanted);
  });

  /**
   * The list the keyboard walks: the manual entry if it is offered, then every result.
   *
   * One list so navigation, the highlight and Enter all agree on what is where. Rendering
   * them separately and tracking two highlights is how the manual row ends up taken by an
   * Enter nobody aimed at it.
   *
   * ⚠️ **FIRST, not last** (AndyP, 2026-09-11). The results box scrolls at 18rem, so a
   * bottom-anchored escape hatch is below the fold on exactly the searches that return a
   * long list of near-misses -- the advisor who most needs it is the one who cannot see it.
   *
   * ⚠️ Its position is no longer what keeps it off the default highlight; {@link highlighted}
   * seeking the first SECURITY is. Reordering this list without that rule would hand the
   * advisor a hand-typed ticker for pressing Enter once, which is the behaviour this whole
   * change removed.
   */
  readonly options = computed<readonly PickerOption[]>(() => {
    const results = this.searchResults().map((security) => ({ kind: 'security', security }) as const);
    return this.canAddManually() ? [{ kind: 'manual' } as const, ...results] : results;
  });

  /** Add a row and put the cursor in its search box, so typing can start immediately. */
  addRow(): void {
    this.store.addBuyRow();
    const added = this.store.buyRows()[this.store.buyRows().length - 1];
    if (added) this.focusSearchInput(added.id);
  }

  /**
   * Whether a held position can be topped up from this step.
   *
   * ⚠️ THE PICKER'S OWN RULE, imported rather than restated. Only Mutual Fund, CASH and Equity
   * may be bought here; the structured notes and bonds an account holds cannot. A second copy of
   * that list would drift, and the failure would be a button offering a purchase the picker
   * refuses to find.
   */
  canBuyMore(position: Position): boolean {
    return isBuyableSecurity(position.securityTypeId);
  }

  /**
   * Add a buy row for a position the account already holds, ready for a share count.
   *
   * ⚠️ GOES THROUGH `addBuyRow` + `selectBuySecurity`, the same two calls the picker makes. The
   * row is then indistinguishable from a hand-picked one -- same price, same `securityId`, same
   * clearing rules -- so nothing downstream has to know it came from a shortcut.
   *
   * ⚠️ FOCUSES AN EXISTING ROW rather than adding a second for the same security. Two rows for
   * one holding is not an error the wizard would catch -- `resultingHoldings` merges them on
   * { securityId, ticker } and the total would be right -- so the advisor would see one line on
   * Review & Submit and two here, with no way to tell which they had just added.
   *
   * ⚠️ Matched on `securityId`, falling back to ticker only when the held row has no id. Tickers
   * are reused between securities; the id is the stable key, which is why the merge above uses it.
   *
   * Lands the cursor on SHARES, not the search box: the security is already decided, and shares
   * is the only thing left to say.
   */
  buyMore(position: Position): void {
    const existing = this.store.buyRows().find((row) =>
      position.securityId != null
        ? row.security?.securityId === position.securityId
        : row.security?.ticker === position.ticker);

    if (existing) {
      this.revealRow(existing.id);
      this.focusSharesInput(existing.id);
      return;
    }

    this.store.addBuyRow();
    const added = this.store.buyRows()[this.store.buyRows().length - 1];
    if (!added) return;

    this.store.selectBuySecurity(added.id, {
      securityId: position.securityId,
      ticker: position.ticker,
      description: position.description,
      securityTypeId: position.securityTypeId,
      price: position.price,
      // ⚠️ The LOAD time, because that is when Orion priced this holding. The picker carries the
      // security master's own `priceAsOf`; a held row has no separate quote, so pretending to one
      // would misreport how stale the estimate is.
      priceAsOf: this.store.loadedAt(),
    });

    this.revealRow(added.id);
    this.focusSharesInput(added.id);
  }

  /**
   * Scroll a buy row into view.
   *
   * ⚠️ Needed because the held list above can be long -- the account in the screenshot that
   * prompted this has fourteen positions -- so the row the button just added is usually below
   * the fold. Focusing alone scrolls it into view in most browsers, but only just: the row lands
   * hard against the bottom edge with the shares box half-clipped by the footer. `center` puts
   * it where it can actually be read.
   */
  private revealRow(rowId: string): void {
    setTimeout(() =>
      this.host.nativeElement
        .querySelector(`[data-buy-row="${rowId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  /**
   * Focus an element once the row has re-rendered.
   *
   * Always located by row id rather than position: several rows can be in the same state
   * at once, so anything like "the last input" jumps the cursor to the wrong row.
   * Deferred a tick because the target does not exist until the change is rendered.
   */
  private focusAfterRender(selector: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLInputElement>(selector)?.focus());
  }

  private focusSearchInput(rowId: string): void {
    this.focusAfterRender(`[data-security-picker="${rowId}"] input`);
  }

  private focusSharesInput(rowId: string): void {
    this.focusAfterRender(`[data-shares-input="${rowId}"]`);
  }

  /**
   * Land the cursor on the amount box after a manual entry.
   *
   * The shares box stays disabled on a hand-typed row -- nothing priced the ticker -- so the
   * amount is the only thing left to say, and it is a validation error until it is said.
   */
  private focusAmountInput(rowId: string): void {
    this.focusAfterRender(`[data-amount-input="${rowId}"]`);
  }

  /**
   * Re-open the list on a row left holding unresolved text.
   *
   * ⚠️ Needed only because closing stopped committing. Before, an unmatched term became a
   * hand-typed security on the way out, so a row was never sitting there with text and no
   * security -- now it is the normal state of an unfinished row, and clicking back into the
   * box has to bring the list with it. Without this the advisor would have to change the
   * text to get their own options back.
   *
   * ⚠️ Marked DIRTY, unlike {@link reopen}. The text is the advisor's own, already kept once;
   * treating it as a seeded term would let a stray click-in-click-out erase it, which is the
   * one thing this whole change exists to stop doing to them.
   */
  onSearchFocus(rowId: string): void {
    if (this.searchingRowId() === rowId) return;

    const row = this.store.buyRows().find((r) => r.id === rowId);
    const term = row?.pendingTerm ?? '';
    if (!row || row.security || !term) return;

    this.terms.update((t) => ({ ...t, [rowId]: term }));
    this.searchDirty = true;
    this.searchingRowId.set(rowId);
    this.searchControl.setValue(term);
  }

  onSearchInput(rowId: string, event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.terms.update((t) => ({ ...t, [rowId]: term }));
    this.searchDirty = true;
    this.searchingRowId.set(rowId);
    this.searchControl.setValue(term);
  }

  /** Re-open the picker on a row that already has a security. */
  reopen(rowId: string): void {
    const row = this.store.buyRows().find((r) => r.id === rowId);
    this.previousSecurity = row?.security ?? null;
    this.searchDirty = false;
    this.store.selectBuySecurity(rowId, null);
    // Seed the box with the ticker being replaced, so re-opening a hand-typed row shows
    // what is there to correct rather than making them retype it from memory.
    this.terms.update((t) => ({ ...t, [rowId]: row?.security?.ticker ?? '' }));
    this.searchingRowId.set(rowId);
    this.focusSearchInput(rowId);
  }

  /**
   * Arrow keys move the highlight, Enter picks it, Escape discards, Tab keeps.
   *
   * Arrow and Enter are prevented: otherwise the arrows move the text cursor within the
   * input and Enter submits the surrounding form.
   *
   * Escape and Tab are handled before the empty-results guard, so both still work when
   * nothing was found — which is exactly when the difference matters, because that is
   * when the typed text is a hand-entered ticker rather than a search term. Tab commits
   * it and is deliberately not prevented, so focus still moves on to the share count.
   */
  onSearchKeydown(rowId: string, event: KeyboardEvent): void {
    if (this.searchingRowId() !== rowId) return;

    if (event.key === 'Escape') {
      this.cancelSearch();
      return;
    }
    if (event.key === 'Tab') {
      // Takes the highlight exactly as Enter does (AndyP, 2026-09-11, after trying it the
      // other way). A stricter Tab -- commit only a highlight the advisor had moved onto
      // themselves -- was built first and read as the box refusing a choice that was plainly
      // on screen. The default highlight is visible, so Tab acting on it is not a guess.
      //
      // ⚠️ What actually removed the hazard here was the manual row never being the default,
      // not Tab being fussy: the worst Tab can now do is take a security the advisor can see
      // highlighted, where it used to file whatever text was in the box as hand-typed.
      //
      // Not prevented either way, so focus still moves on to the next control.
      const choice = this.options()[this.highlighted()];
      if (choice) this.take(rowId, choice);
      else this.closeSearch();
      return;
    }

    const options = this.options();
    if (options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight((this.highlighted() + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        // From the first security this lands on the manual row directly above it -- one key
        // to reach the one option nothing selects on its own.
        this.moveHighlight((this.highlighted() - 1 + options.length) % options.length);
        break;
      case 'Enter': {
        // ⚠️ No fallback to index 0. With no results the highlight is -1 and the only option
        // is the manual row, so a fallback would hand the advisor a hand-typed security for
        // pressing Enter once -- exactly the behaviour this replaced.
        const choice = options[this.highlighted()];
        if (!choice) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        this.take(rowId, choice);
        break;
      }
      default:
        break;
    }
  }

  /** Arrowing or hovering onto an option. */
  moveHighlight(index: number): void {
    this.highlighted.set(index);
  }

  /** Take whichever kind of option is under the highlight. */
  private take(rowId: string, option: PickerOption): void {
    if (option.kind === 'security') this.pick(rowId, option.security);
    else this.addManually(rowId);
  }

  /**
   * Commit the typed text as a hand-entered ticker or CUSIP.
   *
   * The ONLY way a row becomes hand-typed now. It used to happen on blur, on Tab and on
   * Enter-with-no-results, none of which the advisor had to aim at -- so a typo became a
   * resolved-looking row whose money silently turned into protected cash. Reaching this
   * takes a click on a row that quotes the text back, or an arrow key onto it.
   */
  addManually(rowId: string): void {
    const term = this.termFor(rowId).trim();
    if (!term) return;

    this.store.enterManualBuySecurity(rowId, term);
    this.terms.update((t) => ({ ...t, [rowId]: '' }));
    this.closeSearch();
    // Entered by AMOUNT, not shares — there is no price to multiply — so the estimate box
    // is where the row is finished.
    this.focusAmountInput(rowId);
  }

  /**
   * Close the results when the click lands outside the picker.
   *
   * Keyed off a `data-security-picker="{rowId}"` attribute on the whole cell rather than
   * a ViewChild, so the cell's own controls — including the button that re-opens a picked
   * row — are inside it by construction. Without that the re-open click would bubble to
   * here and close the list it had just opened.
   *
   * The id is compared, not just the presence of the attribute: every row's cell carries
   * one, so matching on presence alone would leave row 1's results open while the advisor
   * clicked into row 2.
   *
   * Not `(blur)` on the input: blur fires before the result button's click, so picking
   * with the mouse would close the list before the choice registered.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const active = this.searchingRowId();
    if (!active) return;

    const target = event.target as Element | null;
    // A click can re-render this row before the event finishes bubbling to here — which is
    // exactly what happens when the picked-security button opens the picker, because doing
    // so destroys that button. `closest()` on a detached node finds nothing, the click
    // reads as "outside", and the picker closes in the same breath it opened. A target
    // that is no longer in the document cannot tell us where the click landed, so it must
    // not be taken as evidence that it landed elsewhere.
    if (!target?.isConnected) return;

    const cell = target.closest('[data-security-picker]');
    if (cell?.getAttribute('data-security-picker') === active) return;
    this.closeSearch();
  }

  /**
   * End the search, keeping what the advisor typed **as unfinished text**.
   *
   * ⚠️ This used to decide. Unmatched text became a hand-typed security on the way out, so
   * leaving the field was indistinguishable from choosing, and a typo shipped to the Trade
   * Team looking exactly like an intended alternative investment. Now it keeps the text and
   * keeps the row incomplete: `pendingTerm` puts it back in the box and holds Next until the
   * advisor picks from the list or takes `Add "…" manually`.
   *
   * Throwing the text away instead would be the other wrong answer -- a blank row that looks
   * filled, which is what the old commit-on-blur was written to avoid.
   */
  closeSearch(): void {
    const rowId = this.searchingRowId();
    if (!rowId) {
      this.resetSearch();
      return;
    }

    // Snapshot, reset, THEN commit — never commit while the picker still reads as open.
    // Both the sync and async commit paths ask "has the advisor re-opened this row?", and
    // that question answers itself wrongly if the state they are testing has not been
    // cleared yet. Two separate bugs came from committing first; this ordering removes the
    // category rather than the instances.
    const pending: PendingCommit = {
      previous: this.previousSecurity,
      dirty: this.searchDirty,
    };
    this.resetSearch();
    this.commitSearch(rowId, pending);
  }

  /**
   * End the search, discarding what was typed — the Escape gesture.
   *
   * The counterpart to {@link closeSearch}: this is how an advisor backs out of a typo
   * without leaving an unresolved ticker on the row, and how a re-opened row gets its
   * original security back.
   */
  cancelSearch(): void {
    const rowId = this.searchingRowId();
    const previous = this.previousSecurity;
    this.resetSearch();
    if (!rowId) return;

    if (previous) this.store.selectBuySecurity(rowId, previous);
    this.terms.update((t) => ({ ...t, [rowId]: '' }));
    // Escape is the one gesture that means "forget it", so it clears the unfinished text too
    // -- otherwise backing out of a typo would leave the row blocking Next on that typo.
    this.store.setBuyPendingTerm(rowId, '');
  }

  private resetSearch(): void {
    this.searchingRowId.set(null);
    this.searchResults.set([]);
    this.resultsTerm.set(null);
    // -1, not 0: nothing is highlighted until the advisor is looking at a list again.
    this.highlighted.set(-1);
    this.previousSecurity = null;
    this.searchDirty = false;
    // ⚠️ Cleared so the SAME term can be searched again. `distinctUntilChanged` sits on this
    // stream, so a row re-opened on the text it already held would emit nothing and show an
    // empty list -- including no `Add "…" manually`, the one thing that row needs.
    this.searchControl.setValue('', { emitEvent: false });
  }

  /**
   * Turn an un-chosen search term into the row's security.
   *
   * Requires {@link searchDirty}: a term that is only there because {@link reopen} seeded
   * it means the advisor opened the picker and thought better of it, so the row keeps the
   * security it had — downgrading a resolved security to an unresolved one for that would
   * be the worst outcome available.
   *
   * Runs from {@link closeSearch}, which {@link pick} also calls — harmless there, because
   * by then the row has a security and this returns immediately.
   */
  private commitSearch(rowId: string, pending: PendingCommit): void {
    const row = this.store.buyRows().find((r) => r.id === rowId);
    if (!row || row.security) return;

    const typed = this.termFor(rowId).trim();
    if (!typed || !pending.dirty) {
      if (pending.previous) this.store.selectBuySecurity(rowId, pending.previous);
      this.terms.update((t) => ({ ...t, [rowId]: '' }));
      this.store.setBuyPendingTerm(rowId, '');
      return;
    }

    // ⚠️ Hand it to the ROW rather than resolving it. The old version fired a second lookup
    // from here to catch a full ticker typed and tabbed away before the debounce returned,
    // then committed an exact match as the security and anything else as hand-typed. All of
    // that goes: nothing is committed on the way out, so there is no wrong guess left to
    // prevent, and the race it was written for cannot happen. The text simply comes back
    // into the box with the list already there to pick from.
    this.store.setBuyPendingTerm(rowId, typed);
    this.terms.update((t) => {
      const next = { ...t };
      delete next[rowId];
      return next;
    });
  }

  pick(rowId: string, security: SecurityOption): void {
    this.store.selectBuySecurity(rowId, security);
    this.terms.update((t) => ({ ...t, [rowId]: '' }));
    this.closeSearch();
    // Shares are the only thing left to say about this row, and a picked security with
    // no share count is a validation error — so land the cursor where it needs to go.
    // Ticker, Enter, share count, without reaching for the mouse.
    this.focusSharesInput(rowId);
  }

  onShares(rowId: string, event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.store.setBuyShares(rowId, Number.parseFloat(raw));
  }

  /**
   * Whether a row can offer the dollars-to-shares calculator.
   *
   * Needs a resolved security with a usable price -- there is nothing to divide by otherwise.
   * A hand-typed ticker has neither, and does not want it anyway: that row is entered as an
   * amount directly, which is the thing this dialog exists to convert FROM.
   */
  canCalculateShares(row: { security: SecurityOption | null }): boolean {
    return !!row.security && row.security.securityId != null && (row.security.price ?? 0) > 0;
  }

  /**
   * Turn a dollar amount into a share count and put it in the row.
   *
   * ⚠️ Writes through `setBuyShares`, the same entry point the input uses, rather than poking
   * the DOM. The estimated amount is derived from the stored share count, so a figure that
   * reached only the box would show an estimate that disagreed with it.
   *
   * A cancelled dialog closes with null and nothing is written -- distinct from closing with 0,
   * which the dialog refuses to do precisely so it cannot blank a count the advisor already had.
   */
  openShareCalculator(row: { id: string; security: SecurityOption | null }): void {
    const security = row.security;
    if (!security || (security.price ?? 0) <= 0) return;

    const data: ShareCalculatorData = {
      ticker: security.ticker,
      description: security.description,
      price: security.price as number,
      priceAsOf: security.priceAsOf,
    };

    const ref = this.dialog.open<ShareCalculatorModalComponent, ShareCalculatorData, number | null>(
      ShareCalculatorModalComponent,
      { data, autoFocus: 'first-tabbable' },
    );

    ref.afterClosed()
      .pipe(take(1))
      .subscribe((shares) => {
        if (shares != null && shares > 0) this.store.setBuyShares(row.id, shares);
      });
  }

  /**
   * A hand-typed amount for its input box -- two decimal places, blank when there is none.
   *
   * Mirrors `valueInput` on the allocation grid, which does the same job for the Allocated
   * Value box, so the two money columns read alike. The advisor is checking these against
   * totals rendered by the currency pipe, and a bare `2500` beside `$2,500.00` reads as a
   * different kind of figure.
   *
   * Blank rather than `0.00` for an absent amount: the row shows a red "Enter an amount"
   * beside it, and a zero would dress that up as an answer. `setBuyAmount` maps anything at or
   * below zero to null, so zero never reaches here as a stored figure.
   *
   * ⚠️ Not sufficient on its own -- see `appTwoDecimals` on the input. This formats whatever
   * the re-render writes, which covers a real edit; retyping the SAME figure fires no `change`
   * event, so nothing re-renders and the raw text stays on screen.
   */
  amountInput(value: number | null): string {
    return value == null || value === 0 ? '' : value.toFixed(2);
  }

  /**
   * The dollar figure on a hand-typed row.
   *
   * An emptied box passes null rather than 0, so clearing it leaves it clear while the
   * advisor retypes instead of refilling with a zero they then have to delete.
   */
  onAmount(rowId: string, event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    this.store.setBuyAmount(rowId, raw === '' ? null : Number.parseFloat(raw));
  }
}
